;(function() {
"use strict";

angular.module('core.AbstractResource', ['core.assert', 'core.HttpResource'])
  .factory('AbstractResource', AbstractResourceFactory);

AbstractResourceFactory.$inject = ['$q', '$window', 'assert', 'HttpResource'];
function AbstractResourceFactory($q, $window, assert, HttpResource) {
  const { _ } = $window;

  /**
   * @classdesc
   * Provides an interface for interacting with a RESTful API. Requires a URL, and exposes
   * optional configuration of the {@link HttpResource} class.
   *
   * This class is intended to be sub-classed by individual resources, or additional resource
   * abstractions.
   *
   * @property {number|string} $lookupBy - PK for making resource requests
   * @property {HttpResource} $resource - The configured {HttpResource} class
   * @property {object} $meta - Handling of http responses and url formatting
   */
  class AbstractResource {
    /**
     * @param {string} url - See {@link HttpResource} "url"
     * @param {object} [options]
     * @param {object} [options.meta=META_DEFAULTS]
     * @param {string} [options.meta.responseKey='data'] - The key of a JSON response
     * @param {object} [options.resourceActions] - See {@link HttpResource} "defaults"
     *
     * @example <caption>Basic resource with no customization (80% of use cases)</caption>
     * class ExampleResource extends AbstractResource {
     *   constructor(properties) {
     *     super('/api/example-resources/:id', { meta: { responseKey: 'examples' } });
     *     this.initialize(properties);
     *   }
     * }
     *
     * @example <caption>Basic resource with custom lookup field</caption>
     * class ExampleResource extends AbstractResource {
     *   constructor(properties) {
     *     super('/api/example-resources/:code', { resourceActions: { lookupField: 'code' } });
     *     this.initialize(properties);
     *   }
     * }
     *
     * @example <caption>Basic resource with cached retrieve/list requests</caption>
     * const resourceActions = {
     *   retrieve: { cache: true },
     *   list: { cache: $cacheFactory('ExampleResource') },
     * };
     * class ExampleResource extends AbstractResource {
     *   constructor(properties) {
     *     super(
     *       '/api/example-resources/:id',
     *       {
     *         resourceActions,
     *         meta: { responseKey: 'examples' },
     *       }
     *     );
     *     this.initialize(properties);
     *   }
     * }
     */
    constructor(url, options = {}) {
      Object.defineProperty(this, '$meta', {
        value: angular.extend({}, AbstractResource.META_DEFAULTS, options.meta),
      });

      Object.defineProperty(this, '$resource', {
        value: new HttpResource(url, this.$meta.lookupField, options.resourceActions),
      });

      const lookupBy = () => this[this.$meta.lookupField];
      Object.defineProperty(this, '$lookupBy', {
        get: lookupBy,
      });

      this.isSaving = false;
    }

    /**
     * Create a new resource from the state of this object.
     *
     * @param {object} [overrides] - See {@link HttpResource#create} "overrides"
     *
     * @returns {Promise} The promise object resolves the created resource, which will be the same
     *   as the object the method was invoked on.
     *
     * @example <caption>Persist this unsaved resource</caption>
     * const exampleResource = new ExampleResource();
     * exampleResource.name = 'Untitled Example Resource';
     * exampleResource.create().then(() => {
     *   console.log(exampleResource.id);
     *   >>> {number}
     * });
     */
    create(overrides) {
      this.preSave();
      const request = this.getRequestBody();
      this.isSaving = true;
      return this.$resource.create(request, overrides)
        .then((httpResponse) => {
          const [properties] = this.getResponseBody(httpResponse);
          this.initialize(properties);
          this.postSave();
          return this;
        })
        .finally(() => {
          this.isSaving = false;
        });
    }

    /**
     * Fetch the resource from the API by ID.
     *
     * @param {string} [include] - An optional comma-separated sub-resource inclusion list
     * @param {object} [overrides] - See {@link HttpResource#retrieve}
     *
     * @returns {Promise} The promise object resolves the resource, which will be the same
     *   as the object the method was invoked on.
     *
     * @example <caption>Retrieve a resource with a custom sub-resource inclusion list</caption>
     * const exampleResource = new ExampleResource({ id: 1 });
     * exampleResource.get('modifiedBy').then(() => {
     *   console.log(exampleResource.modifiedBy);
     *   >>> {User}
     * });
     */
    get(include, overrides) {
      assert(angular.isDefined(this.$lookupBy), `[AbstractResource#get] "${this.$meta.lookupField}" must be defined`);

      return this.$resource.retrieve(this.$lookupBy, { include }, overrides)
        .then((httpResponse) => {
          const customResources = this.getResponseBody(httpResponse);
          assert(customResources.length === 1, `[AbstractResource#get] receieved ${customResources.length} resources instead of the expected 1`);
          this.initialize(customResources[0]);
          return this;
        });
    }

    /**
     * Save the current state of a resource.
     *
     * @param {object} [overrides] - See {@link HttpResource#update}
     *
     * @returns {Promise} The promise object resolves the resource, which will be the same
     *   as the object the method was invoked on.
     *
     * @example <caption>Save a change to a resource</caption>
     * const exampleResource = new ExampleResource({ id: 1 });
     * exampleResource.name += ' (updated)';
     * exampleResource.update().then(() => {
     *   console.log(exampleResource.name);
     *   >>> {string}
     * });
     */
    update(overrides) {
      assert(angular.isDefined(this.$lookupBy), `[AbstractResource#update] "${this.$meta.lookupField}" must defined on the resource`);
      this.preSave();
      const request = this.getRequestBody();
      this.isSaving = true;
      return this.$resource.update(this.$lookupBy, request, overrides)
        .then((httpResponse) => {
          const [properties] = this.getResponseBody(httpResponse);
          this.initialize(properties);
          this.postSave();
          return this;
        })
        .finally(() => {
          this.isSaving = false;
        });
    }

    /**
     * Save the current state of a resource, if there is no identifier, it will create a new
     * resource, otherwise it will attempt to update the resource.
     *
     * @param {object} [overrides] - See {@link HttpResource#update} or {@link HttpResource#create}
     *
     * @example <caption>Retrieve and save a change to a resource</caption>
     * const exampleResource = new ExampleResource(properties);
     * exampleResource.name += ' (updated)';
     * exampleResource.save().then(() => {
     *   console.log(exampleResource.name);
     *   >>> {string}
     * });
     */
    save(overrides) {
      return this.$lookupBy ? this.update(overrides) : this.create(overrides);
    }

    /**
     * Delete the resource, and set the "deletedAt" property to a new timestamp to reflect the
     * resource has been deleted.
     *
     * @param {object} [overrides] - See {@link HttpResource#delete}
     *
     * @returns {Promise} The promise object resolves nothing, but changes the state of the resource
     *   the method was called on.
     *
     * @example <caption>Delete a resource</caption>
     * const exampleResource = new ExampleResource({ id: 1 });
     * exampleResource.delete().then(() => {
     *   console.log(example.deletedAt)
     *   >>> {date|string}
     * });
     */
    delete(overrides) {
      assert(angular.isDefined(this.$lookupBy), `[AbstractResource#delete] "${this.$meta.lookupField}" must defined on the resource`);
      return this.$resource.delete(this.$lookupBy, overrides)
        .then(() => {
          this.deletedAt = new Date();
          return this;
        });
    }

    /**
     * Instances of this can class be initialized multiple times (ideally without side effects).
     * This is most relied on when completing a "save" or when a "dehydrated" instance of a resource
     * has called "get" and the instance needs to be hydrated with data from the API.
     *
     * Can be overridden to prevent extending the result onto the instance, otherwise can be built
     * upon for constructing instances of sub-resources etc.
     *
     * @param {object} [properties] - The properties of the resource to add to this instance
     *
     * @example <caption>Construct sub-resources during initialization</caption>
     * class ExampleResource extends AbstractResource {
     *   initialize(properties) {
     *     super.initialize(properties); // Default behavior
     *     // Construct resources
     *     this.createdAt = new Date(this.createdAt);
     *     this.createdBy = new User(this.createdBy);
     *     // Construct only if present
     *     this.folder = angular.isObject(this.folder) ? new Folder(this.folder) : this.folder;
     *   }
     * }
     *
     * @example <caption>Prevent default behavior of extending properties onto "this"</caption>
     * class ExampleResource extends AbstractResource {
     *   initialize(properties) {
     *     this.name = properties.name.toUpperCase(); // Mutate/transform individual properties
     *     this.data = processProperties(properties); // Or process with a non-member function
     *   }
     * }
     */
    initialize(properties) {
      angular.extend(this, properties);
    }

    /**
     * Form a request body for a POST or PUT/PATCH request. Use properties of the instance, while
     * respecting the {@link AbstractResource.READ_ONLY_FIELDS} property of the constructor as well
     * as a few default conversions.
     *
     * @returns {object} The object should represent the "flat" representation of the resource
     *   without sub-resources.
     */
    getRequestBody() {
      const request = _.omit(this, this.constructor.READ_ONLY_FIELDS);

      Object.keys(request).forEach((key) => {
        const value = request[key];
        if (angular.isDate(value)) {
          request[key] = value.toISOString();
        } else if (angular.isObject(value) && _.has(value, 'id')) {
          request[key] = value.id;
        }
      });

      delete request[this.$meta.lookupField];

      return request;
    }

    /**
     * Retrieve the relevant information for this resource from the API response.
     *
     * @param {object} httpResponse - The response object from angular's $http
     *
     * @returns {*} The return value is likely an array or object, but could be of any type.
     *
     * @example <caption>Override to parse Geobuf result</caption>
     * class ExampleResource extends AbstractResource {
     *   getResponseBody(httpResponse) {
     *     return geobuf.decode(new Pbf(httpResponse.data));
     *   }
     * }
     */
    getResponseBody(httpResponse) {
      return httpResponse.data[this.$meta.responseKey];
    }

    /**
     * Hook for a synchronous custom action that occurs before a save (create/update) is made.
     */
    preSave() {} // eslint-disable-line class-methods-use-this

    /**
     * Hook for a synchronous custom action that occurs after a save (create/update) has completed.
     */
    postSave() {} // eslint-disable-line class-methods-use-this

    /**
     * Are the two resources representing the same thing.
     *
     * @param {AbstractResource} comparisonResource - The resource to compare to "this" one
     *
     * @returns {boolean}
     */
    isSameResource(comparisonResource) {
      const isNotDefined = !angular.isObject(comparisonResource);
      const doesNotHaveResource = !angular.isObject(comparisonResource.$resource);
      if (isNotDefined || doesNotHaveResource) return false;
      const matchesResourceUrl = this.$resource.url === comparisonResource.$resource.url;
      const matchesLookup = this.$lookupBy === comparisonResource.$lookupBy;
      return matchesResourceUrl && matchesLookup;
    }

    /**
     * Create a new instance of the resource and send it to the API.
     *
     * @param {object} properties - The initial properties of the resource
     * @param {object} [overrides] - See {@link AbstractResource#create}
     *
     * @returns {Promise} The promise resolves the new resource that has been created
     */
    static create(properties, overrides) {
      const CustomResource = this;
      return new CustomResource(properties).create(overrides);
    }

    /**
     * Look for resources matching the provided criteria. Throw an error if anything besides one
     * resource matched the parameters.
     *
     * @param {object} [params] - key/value pairs to turn into a query string
     * @param {object} [overrides] - See {@link AbstractResource.list}
     *
     * @returns {Promise} The promise object resolves only the single resource
     *
     * @example
     * ExampleResource.one({ isFeatured: true });
     */
    static one(params, overrides) {
      const CustomResource = this;
      return CustomResource.list(params, overrides).then((customResources) => {
        const [firstResource] = customResources;
        return customResources.length === 1 ? firstResource : $q.reject(customResources);
      });
    }

    /**
     * Retrieve resources from the server matching the provided criteria. Throw an error if the API
     * responds with more than one resource (0 is allowed).
     *
     * @param {object} [params] - key/value pairs to turn into a query string
     * @param {object} [overrides] - See {@link AbstractResource.one}
     *
     * @returns {Promise} The promise object resolves either a single implemented class instance
     *   or null.
     *
     * @example
     * ExampleResource.oneOrNone({ isFeatured: true });
     */
    static oneOrNone(params, overrides) {
      const CustomResource = this;
      return CustomResource.one(params, overrides).catch(customResources => (
        customResources.length !== 0 ? $q.reject(customResources) : null
      ));
    }

    /**
     * Retrieve a single resource by looking it up by its identifier.
     *
     * @param {number|string} id - The identifier for a single resource
     * @param {string} [include] - An optional comma-separated sub-resource inclusion list
     * @param {object} [overrides] - See {@link HttpResource#retrieve}
     *
     * @returns {Promise} The promise object resolves a single instance of the implemented class.
     *
     * @example
     * ExampleResource.get(1).then((exampleResource) => {
     *   // Do something with "exampleResource"
     * });
     *
     * @example <caption>Customized sub-resource inclusion list (overrides defaults)</caption>
     * ExampleResource.get(1, 'createdBy,parent').then((exampleResource) => {
     *   // Do something with "exampleResource"
     * });
     */
    static get(lookupBy, include, overrides) {
      assert(angular.isDefined(lookupBy), '[AbstractResource.get] "lookupBy" must be defined');
      const CustomResource = this;
      const customResource = new CustomResource();

      return customResource.$resource.retrieve(lookupBy, { include }, overrides)
        .then((httpResponse) => {
          const customResources = customResource.getResponseBody(httpResponse);
          assert(customResources.length === 1, `[AbstractResource.get] receieved ${customResources.length} resources instead of the expected 1`);
          return new CustomResource(customResources[0]);
        });
    }

    /**
     * Retrieve a list of resources that match the provided criteria as interpreted by the API.
     *
     * @param {object} [params] - key/value pairs to turn into a query string
     * @param {object} [overrides] - See {@link HttpResource#list}
     *
     * @returns {Promise} The promise object resolves an array of class instances.
     *
     * @example
     * ExampleResource.list({ orderBy: 'name', isPublished: true }).then((exampleResources) => {
     *   // Do something with "exampleResources"
     * });
     */
    static list(params, overrides) {
      assert(angular.isObject(params) || angular.isUndefined(params), '[AbstractResource.list] "params" must be an object or undefined');

      const CustomResource = this;
      const customResource = new CustomResource();

      return customResource.$resource.list(params, overrides)
        .then(httpResponse => (
          customResource.getResponseBody(httpResponse).map(resource => new CustomResource(resource))
        ));
    }

    /**
     * Retrieve all resources that match the provided criteria as interpreted by the API.
     * If more than one page of resources is specified in the response, all pages will be retrieved
     * prior to resolving the promise.
     *
     * @param {object} [params] - key/value pairs to turn into a query string
     * @param {object} [overrides] - See {@link HttpResource#list}
     *
     * @returns {Promise} The promise object resolves an array of class instances.
     *
     * @example
     * ExampleResource.all({ orderBy: 'name', isPublished: true }).then((exampleResources) => {
     *   // Do something with "exampleResources"
     * });
     */
    static all(params = {}, overrides) {
      const CustomResource = this;
      const customResource = new CustomResource();
      const paramsWithDefaults = _.defaults(
        params,
        { itemsPerPage: AbstractResource.ALL_DEFAULT_PER_PAGE }
      );

      return customResource.$resource.list(paramsWithDefaults, overrides).then((httpResponse) => {
        const meta = httpResponse.data.meta.pagination || {};
        let resources = customResource.getResponseBody(httpResponse).map(
          resource => new CustomResource(resource)
        );
        let promise = $q.when();
        if (meta.page < meta.totalPages) {
          const pages = _.range(meta.page + 1, meta.totalPages + 1);
          pages.forEach((page) => {
            promise = promise.then(() => {
              const pagedParams = _.chain(params).clone().extend({ page }).value();
              return CustomResource.list(pagedParams, overrides).then((response) => {
                resources = resources.concat(response);
              });
            });
          });
        }

        return promise.then(() => resources);
      });
    }

    /**
     * Fields that will be ignored while forming the body of a request for "create"/"update".
     *
     * @example <caption>Add additional read only fields to this resource</caption>
     * class ExampleResource extends AbstractResource {
     *   static get READ_ONLY_FIELDS() {
     *     return super.READ_ONLY_FIELDS.concat(['my_private_property_1', 'my_private_property_2']);
     *   }
     * }
     */
    static get READ_ONLY_FIELDS() {
      return [
        '$meta',
        '$resource',
        'createdAt',
        'createdBy',
        'deletedAt',
        'deletedBy',
        'isSaving',
        'modifiedAt',
        'modifiedBy',
      ];
    }
  }

  AbstractResource.META_DEFAULTS = {
    responseKey: 'data',
    lookupField: 'id',
  };

  AbstractResource.ALL_DEFAULT_PER_PAGE = 100;

  return AbstractResource;
}
}());
